import * as Sentry from '@sentry/vue'
import axios, { type CreateAxiosDefaults } from 'axios'

import { config } from '@/lib/helpers'
import { observeAdBlockerFailure } from '@/lib/services/ad-blocker.service'
import { notifyUserBanned } from '@/lib/services/banned-user.service'

export const baseURL = config.metapicApiUrl

let demoAdapter:
  | ((config: import('axios').InternalAxiosRequestConfig) => Promise<import('axios').AxiosResponse>)
  | undefined
if (import.meta.env.VITE_DEMO_MODE === 'true') {
  const { createDemoAdapter } = await import('@/demo/demo-adapter')
  demoAdapter = createDemoAdapter()
}

const createAxios = (headers?: CreateAxiosDefaults['headers']) => {
  const axiosInstance = axios.create({
    baseURL,
    headers,
  })

  if (demoAdapter) {
    axiosInstance.defaults.adapter = demoAdapter
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
        notifyUserBanned()
      }

      if (config.environment === 'production') {
        if (error.response?.data && error.code !== 404 && error.code !== 403) {
          Sentry.captureException(
            new Error(`Event: ${typeof error === 'string' ? error : error?.message}`),
            {
              // @ts-expect-error todo fix me
              name: error.message,
              message: error.response.data.message,
              type: 'API error',
              context: {
                code: error.code,
                message: error.response.data.message,
                file: error.response.data.file,
                exception: error.response.data.exception,
                url: error?.request?.responseURL.split('?')[0],
                method: error.response.config.method,
              },
            },
          )
        } else {
          Sentry.captureException(
            new Error(`Event: ${typeof error === 'string' ? error : error?.message}`),
          )
        }
      }

      void observeAdBlockerFailure(error)

      const response = error?.response?.data
        ? { ...error.response.data, status: error.status }
        : error

      return Promise.reject(response as Error)
    },
  )

  return axiosInstance
}

export default createAxios
